import { Button, Modal } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { useGuardedNavigate } from "@/lib/utils/unsaved-changes";
import { getYardState, type YardState } from "../staff/yard-state";
import { AnnuaireLink, YardScreenHeader } from "./components";
import { ContractGroupForm } from "./contract-group-form";
import { ContractTeamsDrawer } from "./contract-teams-drawer";
import {
  type ContractGroup,
  ContractGroupsToolbar,
  ContractsSlideOver,
  ReferentHistoryDeleteDialog,
  type ReferentHistoryRemoval,
} from "./contracts-panel";
import { DeactivateCollaboratorModal } from "./deactivate-collaborator-modal";
import {
  type Building,
  OversightBuildingsGrid,
} from "./oversight-buildings-grid";
import { OversightContractReferentForm } from "./oversight-contract-referent-form";
import {
  employeeName,
  type OversightContractRow,
  OversightContractsEditablePanel,
} from "./oversight-contracts-editable-panel";

type DeleteTarget =
  | { kind: "contract"; id: number; label: string }
  | { kind: "group"; id: number; label: string }
  | {
      kind: "referent";
      contractId: number;
      employeeContractId: number;
      label: string;
    };

/**
 * Screen 909 — "Contrats et équipes donneurs d'ordre".
 * Same design as screen 910: the main area shows the building-scope grid
 * (contracts × buildings) and the contracts live in a collapsible
 * overlay panel with inline draft editing, deletion before mise en
 * service and deactivation after.
 */
export function YardDonneursOrdrePage() {
  const { yardId } = useParams<{ yardId: string }>();
  const navigate = useGuardedNavigate();
  const numericYardId = Number(yardId);

  const [yardName, setYardName] = useState<string | null>(null);
  const [yardState, setYardState] = useState<YardState | null>(null);
  const [isConceptionRealisation, setIsConceptionRealisation] = useState(false);
  const [yardMoaCompany, setYardMoaCompany] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [contracts, setContracts] = useState<OversightContractRow[]>([]);
  const [groups, setGroups] = useState<ContractGroup[]>([]);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [loading, setLoading] = useState(true);
  // After mise en service, contracts can no longer be freely edited/deleted
  // (only deactivated). Drives the draft-edit mode gating.
  const [isCommissioned, setIsCommissioned] = useState(false);

  // Group creation form; existing groups are edited in the contracts panel's draft
  const [groupFormOpen, setGroupFormOpen] = useState(false);

  // Referent form state
  const [referentFormOpen, setReferentFormOpen] = useState(false);
  const [referentContract, setReferentContract] =
    useState<OversightContractRow | null>(null);
  // Referent being deactivated on their contract (successors asked first)
  const [deactivatingReferent, setDeactivatingReferent] =
    useState<OversightContractRow | null>(null);

  // Teams drawer state (kept by id so it reflects refreshed data)
  const [teamsContractId, setTeamsContractId] = useState<number | null>(null);

  // Collapsible contracts panel, open by default (same design as 910).
  const [contractsPanelOpen, setContractsPanelOpen] = useState(true);
  const [groupsExpanded, setGroupsExpanded] = useState(false);
  const [isContractsDirty, setIsContractsDirty] = useState(false);

  // Former referent whose « x » was clicked (removal confirmed first)
  const [historyRemoval, setHistoryRemoval] =
    useState<ReferentHistoryRemoval | null>(null);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchYard = useCallback(async () => {
    const { data } = await apiClient.GET("/api/admin/yards/{id}", {
      params: { path: { id: numericYardId } },
    });
    if (data) {
      const found = data.data.yard;
      setYardName(found.name);
      setYardState(getYardState(found));
      setIsConceptionRealisation(found.type === "conception_realisation");
      setYardMoaCompany(found.moaCompany);
      setIsCommissioned(isNotNil(found.comissioningDate));
    }
  }, [numericYardId]);

  const fetchData = useCallback(async () => {
    const { data, error } = await apiClient.GET(
      "/api/admin/yards/oversight-contracts",
      { params: { query: { yardId: numericYardId } } },
    );
    if (error) {
      toaster.error({
        title: "Erreur",
        description: "Échec du chargement des contrats donneurs d'ordre",
      });
      setLoading(false);
      return;
    }
    setContracts(
      data.data.contracts.map((c) => ({
        id: c.id,
        type: c.type,
        shortName: c.shortName,
        isActive: c.isActive,
        delayForNoticeDays: c.delayForNoticeDays,
        groupId: c.groupId,
        performer: c.performer,
        customer: c.customer,
        group: c.group,
        referent: c.referent,
        referentHistory: c.referentHistory,
        batiments: c.batiments,
        teams: c.teams,
      })),
    );
    setGroups(data.data.groups);
    setBuildings(data.data.buildings);
    setLoading(false);
  }, [numericYardId]);

  useEffect(() => {
    fetchYard();
    fetchData();
  }, [fetchYard, fetchData]);

  // After mise en service contracts can no longer be deleted, only
  // deactivated. There is no reactivation (PO, N74, 30/10).
  const deactivateContract = async (contract: OversightContractRow) => {
    const { error } = await apiClient.PATCH("/api/admin/contracts/{id}", {
      params: { path: { id: contract.id } },
      body: { isActive: false },
    });
    if (error) {
      toaster.error({
        title: "Erreur",
        description: error.error || "Échec de la mise à jour du contrat",
      });
      return;
    }
    toaster.success({ title: "Succès", description: "Contrat désactivé" });
    fetchData();
  };

  const openReferentForm = (contract: OversightContractRow) => {
    setReferentContract(contract);
    setReferentFormOpen(true);
  };

  const confirmDelete = async () => {
    if (isNil(deleteTarget)) return;
    setIsDeleting(true);

    let apiError: { error?: string } | undefined;
    if (deleteTarget.kind === "contract") {
      ({ error: apiError } = await apiClient.DELETE(
        "/api/admin/contracts/{id}",
        { params: { path: { id: deleteTarget.id } } },
      ));
    } else if (deleteTarget.kind === "group") {
      ({ error: apiError } = await apiClient.DELETE(
        "/api/admin/yards/contract-groups/{id}",
        { params: { path: { id: deleteTarget.id } } },
      ));
    } else {
      ({ error: apiError } = await apiClient.DELETE(
        "/api/admin/yards/oversight-contracts/{contractId}/referent/{employeeContractId}",
        {
          params: {
            path: {
              contractId: deleteTarget.contractId,
              employeeContractId: deleteTarget.employeeContractId,
            },
          },
        },
      ));
    }

    setIsDeleting(false);
    if (isNotNil(apiError)) {
      toaster.error({
        title: "Erreur",
        description: apiError.error || "Échec de la suppression",
      });
      return;
    }
    toaster.success({ title: "Succès", description: "Suppression effectuée" });
    setDeleteTarget(null);
    fetchData();
  };

  const canAddContracts =
    yardState === "preparation" || yardState === "en_service";

  if (loading) {
    return (
      <div className="container mx-auto flex h-full items-center justify-center p-6 text-gray-500">
        Chargement...
      </div>
    );
  }

  return (
    <div className="container mx-auto flex h-full min-h-0 flex-col overflow-hidden p-6">
      <YardScreenHeader
        yardName={yardName}
        yardState={yardState}
        screenTitle="Contrats et équipes donneurs d'ordre"
        onBack={() => navigate(`/admin/yards/${numericYardId}`)}
        backLabel="Retour au menu de paramétrage"
        actions={<AnnuaireLink className="ml-auto" />}
      />

      {/* Grille périmètre + panneau contrats : le panneau glisse en overlay
          par-dessus la grille (même design que l'écran 910). */}
      <div className="relative flex min-h-0 flex-1 items-start gap-6 overflow-hidden">
        <section className="flex h-full min-w-0 flex-1 flex-col">
          {/* Same one-line bar as the 910 grid toolbar (same height). */}
          <div className="mb-3 flex min-h-11 shrink-0 items-center gap-1 rounded-lg border border-zinc-200 p-2 dark:border-zinc-800">
            <h3
              className="cursor-help whitespace-nowrap text-sm font-semibold text-zinc-900 dark:text-zinc-100"
              title="Contrats (lignes) × bâtiments (colonnes). Cochez les bâtiments traités dans le cadre de chaque contrat ; « Tout » coche toute la ligne."
            >
              Périmètre contrats
            </h3>
          </div>
          <div className="min-h-0 flex-1">
            <OversightBuildingsGrid
              contracts={contracts}
              buildings={buildings}
              onSuccess={fetchData}
            />
          </div>
        </section>

        <ContractsSlideOver
          open={contractsPanelOpen}
          onOpenChange={setContractsPanelOpen}
          isDirty={isContractsDirty}
        >
          <OversightContractsEditablePanel
            yardId={numericYardId}
            contracts={contracts}
            groups={groups}
            groupsExpanded={groupsExpanded}
            groupsNote={
              isConceptionRealisation
                ? undefined
                : "Hors conception-réalisation, un groupement ne peut pas mêler entreprises de travaux et donneurs d'ordre : les groupements d'entreprises de travaux (écran 910) ne sont pas proposés ici."
            }
            onDeleteGroup={(group) =>
              setDeleteTarget({
                kind: "group",
                id: group.id,
                label: `le groupement ${group.name}`,
              })
            }
            yardMoaCompany={yardMoaCompany}
            isCommissioned={isCommissioned}
            canAddContracts={canAddContracts}
            onDirtyChange={setIsContractsDirty}
            onSuccess={fetchData}
            onManageTeams={(contract) => setTeamsContractId(contract.id)}
            onSetReferent={openReferentForm}
            onRemoveReferent={(contract) => {
              if (isNil(contract.referent)) return;
              setDeleteTarget({
                kind: "referent",
                contractId: contract.id,
                employeeContractId: contract.referent.id,
                label: `le référent ${employeeName(contract.referent)}`,
              });
            }}
            onDeleteHistory={setHistoryRemoval}
            onDeactivateReferent={setDeactivatingReferent}
            onDeactivateContract={deactivateContract}
            onDelete={(contract) =>
              setDeleteTarget({
                kind: "contract",
                id: contract.id,
                label: `le contrat ${contract.performer.name}`,
              })
            }
            toolbar={
              <ContractGroupsToolbar
                count={groups.length}
                expanded={groupsExpanded}
                onToggle={() => setGroupsExpanded((v) => !v)}
                onCreate={() => setGroupFormOpen(true)}
              />
            }
          />
        </ContractsSlideOver>
      </div>

      {/* Forms */}
      <ContractGroupForm
        open={groupFormOpen}
        onOpenChange={setGroupFormOpen}
        yardId={numericYardId}
        onSuccess={fetchData}
      />

      {isNotNil(referentContract) && (
        <OversightContractReferentForm
          open={referentFormOpen}
          onOpenChange={setReferentFormOpen}
          contractId={referentContract.id}
          performerId={referentContract.performer.id}
          referent={referentContract.referent}
          onSuccess={fetchData}
        />
      )}

      {isNotNil(deactivatingReferent?.referent) && (
        <DeactivateCollaboratorModal
          open
          onOpenChange={(open) => {
            if (!open) setDeactivatingReferent(null);
          }}
          employee={{
            id: deactivatingReferent.referent.employeeId,
            name: employeeName(deactivatingReferent.referent),
          }}
          contractId={deactivatingReferent.id}
          onSuccess={fetchData}
        />
      )}

      {isNotNil(teamsContractId) &&
        (() => {
          const teamsContract = contracts.find((c) => c.id === teamsContractId);
          if (isNil(teamsContract)) return null;
          return (
            <ContractTeamsDrawer
              open
              onOpenChange={(open) => {
                if (!open) setTeamsContractId(null);
              }}
              yardId={numericYardId}
              contractId={teamsContractId}
              contractType={teamsContract.type}
              performerId={teamsContract.performer.id}
              performerName={teamsContract.performer.name}
              teams={teamsContract.teams}
              contractDelayForNoticeDays={teamsContract.delayForNoticeDays}
              isCommissioned={isCommissioned}
              canAddTeams={canAddContracts}
              onSuccess={fetchData}
            />
          );
        })()}

      <ReferentHistoryDeleteDialog
        removal={historyRemoval}
        onClose={() => setHistoryRemoval(null)}
        onDeleted={fetchData}
      />

      {/* Delete confirmation */}
      <Modal.Root
        open={isNotNil(deleteTarget)}
        onOpenChange={(details) => {
          if (!details.open) setDeleteTarget(null);
        }}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content size="sm">
              <Modal.Header>
                <Modal.Title>Confirmer la suppression</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  Voulez-vous vraiment supprimer{" "}
                  <span className="font-medium">{deleteTarget?.label}</span> ?
                  Cette action est irréversible.
                </p>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  outline
                  onClick={() => setDeleteTarget(null)}
                  disabled={isDeleting}
                >
                  Annuler
                </Button>
                <Button
                  color="red"
                  onClick={confirmDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Suppression..." : "Supprimer"}
                </Button>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>
    </div>
  );
}
